import { useState } from "react";
import { Mail, Check, Clock, BellOff, Eye, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STEP_LABELS = {
  j3: "J+3 · Relance",
  j7: "J+7 · Offre -6%",
  j15: "J+15 · Feedback",
};
const STEP_ORDER = ["j3", "j7", "j15"];

export function LeadSequencePanel({ lead, reload }) {
  const [busy, setBusy] = useState("");
  const sent = lead.seq_sent || [];
  const unsubscribed = !!lead.seq_unsubscribed;

  const preview = async (step) => {
    setBusy(`preview-${step}`);
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/sequence/preview`, { params: { step } });
      // Render in a sandboxed blob URL (no document.write) to avoid XSS surface.
      const doc = `<!doctype html><meta charset="utf-8"><title>${data.subject}</title>${data.html}`;
      const url = URL.createObjectURL(new Blob([doc], { type: "text/html" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Échec de la prévisualisation");
    } finally {
      setBusy("");
    }
  };

  const sendNow = async (step) => {
    setBusy(`send-${step}`);
    try {
      await api.post(`/admin/leads/${lead.id}/sequence/send`, null, { params: { step } });
      toast.success(`E-mail ${STEP_LABELS[step]} envoyé`);
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6" data-testid="sequence-panel">
      <h2 className="font-head font-bold text-[#1a2d56] mb-1 flex items-center gap-2">
        <Mail size={16} /> Séquence e-mail automatisée
      </h2>
      <p className="text-xs text-[#9aa6c0] mb-4">3 e-mails personnalisés (IA), envoyés automatiquement.</p>

      {unsubscribed && (
        <div className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="sequence-unsubscribed">
          <BellOff size={16} className="shrink-0" /> Ce prospect s'est désinscrit. La séquence est arrêtée.
        </div>
      )}

      <div className="space-y-2.5">
        {STEP_ORDER.map((step) => {
          const isSent = sent.includes(step);
          return (
            <div key={step} className="flex items-center justify-between gap-3 rounded-xl bg-[#f8f9fc] border border-[#eef1f6] p-3" data-testid={`sequence-step-${step}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isSent ? "bg-emerald-100 text-emerald-600" : "bg-[#e2e8f0] text-[#9aa6c0]"}`}>
                  {isSent ? <Check size={15} /> : <Clock size={14} />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1a2d56] truncate">{STEP_LABELS[step]}</p>
                  <p className="text-xs text-[#9aa6c0]">{isSent ? "Envoyé" : "En attente"}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => preview(step)}
                  disabled={busy === `preview-${step}`}
                  data-testid={`sequence-preview-${step}`}
                  title="Prévisualiser"
                  className="w-8 h-8 rounded-lg bg-white border border-[#e2e8f0] text-[#4b5b7c] hover:text-[#1a2d56] flex items-center justify-center disabled:opacity-50"
                >
                  <Eye size={15} />
                </button>
                <button
                  onClick={() => sendNow(step)}
                  disabled={busy === `send-${step}` || unsubscribed}
                  data-testid={`sequence-send-${step}`}
                  title={isSent ? "Renvoyer" : "Envoyer maintenant"}
                  className="w-8 h-8 rounded-lg bg-[#1a2d56] text-white hover:bg-[#24386b] flex items-center justify-center disabled:opacity-40"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
